import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { EVIDENCE_LABEL, RELIABILITY_MINIMUM, powerLocationById } from '../../data/power';
import { freshnessLabel } from '../../utils/power';
import { IconTile, LOCATION_ICON, PowerSkeleton, PowerStatusPill, powerIcons } from './PowerPrimitives';
import type { PowerIconName } from './PowerPrimitives';
export const RELIABILITY_ART = 'https://cdn.magicpatterns.com/patterns/generated-images/49c87225-2c60-4feb-b91c-040036b52d97.jpg';

/** Quick actions. Each one goes somewhere real; none is decorative. */
export function QuickActions({
  onReport,
  onNotify,
  notifying
}: {
  onReport: () => void;
  onNotify: () => void;
  notifying: boolean;
}) {
  const rows: {
    icon: PowerIconName;
    label: string;
    description: string;
    to?: string;
    onClick?: () => void;
    state?: string;
  }[] = [{
    icon: 'map',
    label: 'View power map',
    description: 'Explore on map',
    to: '/power/map'
  }, {
    icon: 'report',
    label: 'Report power',
    description: 'Tell us what you see',
    onClick: onReport
  }, {
    icon: 'location',
    label: 'Power near me',
    description: 'See nearby status',
    to: '/power/nearby'
  }, {
    icon: 'notification',
    label: 'Get notified',
    description: 'When power returns',
    onClick: onNotify,
    state: notifying ? 'On' : undefined
  }];
  return <section aria-labelledby="power-quick-actions" className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4256" data-mp="3n8ybk59dofpy">
      <h2 id="power-quick-actions" className="text-h4 font-semibold text-ink" data-id="element-4257" data-mp="3e9uqwwukbs9c">
        Quick actions
      </h2>
      <ul className="mt-4 space-y-2.5" data-id="element-4258" data-mp="17jn91mhb6q13">
        {rows.map(row => {
        const Icon = powerIcons[row.icon];
        const body = <>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700" data-id="element-4259" data-mp="1cgnebv6c3or5">
                <Icon className="h-4 w-4" aria-hidden data-id="element-4260" data-mp="162838hooqibo" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-4261" data-mp="16540e3spl2z2">
                <span className="block truncate text-body-sm font-medium text-ink" data-id="element-4262" data-mp="114pqeg7jriye">
                  {row.label}
                </span>
                <span className="block truncate text-caption text-ink-subtle" data-id="element-4263" data-mp="w6ob86d8s3s7">
                  {row.description}
                </span>
              </span>
              {row.state && <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ok-100 bg-ok-50 px-2 py-0.5 text-caption font-semibold text-ok-800" data-id="element-4264" data-mp="1ou0ua6k71tgr">
                  <CheckIcon className="h-3 w-3" aria-hidden data-id="element-4265" data-mp="1f2efk08ndc99" />
                  {row.state}
                </span>}
            </>;
        const shell = 'flex min-h-touch w-full items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5 text-left transition-colors duration-150 ease-out hover:border-line-strong hover:bg-canvas';
        return <li key={row.label} data-id="element-4266" data-mp="2k3e09n7h0rnb">
              {row.to ? <Link to={row.to} className={shell} data-id="element-4267" data-mp="2p1p6lnyk4k0s">
                  {body}
                </Link> : <button type="button" onClick={row.onClick} className={shell} data-id="element-4268" data-mp="2u0sd7cu6qyav">
                  {body}
                </button>}
            </li>;
      })}
      </ul>
    </section>;
}

/** Power near me — derived from real distance, stated as straight-line. */
export function PowerNearby({
  limit = 5
}: {
  limit?: number;
}) {
  const {
    homeLocationId,
    getNearbyStatuses
  } = usePower();
  const entries = getNearbyStatuses(homeLocationId, 1);
  return <section aria-labelledby="power-nearby" className="flex flex-col rounded-2xl border border-line bg-surface shadow-panel" data-id="element-4269" data-mp="3hqcxbz22s1lq">
      <div className="p-5 pb-3" data-id="element-4270" data-mp="3lshpsugses6s">
        <h2 id="power-nearby" className="text-h4 font-semibold text-ink" data-id="element-4271" data-mp="3lpbzjbwf9k9h">
          Power near me
        </h2>
        <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-4272" data-mp="ic3rif4mpw39">
          Within 1 km in a straight line, not travel time.
        </p>
      </div>
      {entries.length === 0 ? <p className="px-5 pb-5 text-body-sm text-ink-muted" data-id="element-4273" data-mp="w5l9vwlkx2rp">
          No mapped locations within 1 km have recent reports.
        </p> : <ul className="divide-y divide-line" data-id="element-4274" data-mp="4bj6rdf5lq74">
          {entries.slice(0, limit).map(entry => {
        const Icon = powerIcons[LOCATION_ICON[entry.location.kind]];
        return <li key={entry.location.id} className="flex items-center gap-3 px-5 py-2.5" data-id="element-4275" data-mp="ddiqwelgtaea">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-canvas text-ink-muted" data-id="element-4276" data-mp="8deizcymhnzo">
                  <Icon className="h-4 w-4" aria-hidden data-id="element-4277" data-mp="1or4p0p9aymby" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-4278" data-mp="1jtl0mwiw4t6a">
                  <span className="block truncate text-body-sm font-medium text-ink" data-id="element-4279" data-mp="1jsth4k2kilwd">
                    {entry.location.name}
                  </span>
                  <span className="cz-num block text-caption text-ink-subtle" data-id="element-4280" data-mp="1wbxfgbbah03u">
                    {entry.distanceKm < 0.1 ? 'Less than 100 m away' : `${entry.distanceKm.toFixed(1)} km away`}
                  </span>
                </span>
                <PowerStatusPill state={entry.status.state} size="xs" data-id="element-4281" data-mp="2b0tmc51m072v" />
                <span className="cz-num hidden shrink-0 text-caption text-ink-subtle sm:block" data-id="element-4282" data-mp="2b2o5bdtblp4b">
                  {entry.status.lastConfirmedAt ? freshnessLabel(entry.status.lastConfirmedAt) : 'No data'}
                </span>
              </li>;
      })}
        </ul>}
      <Link to="/power/nearby" className="mt-auto flex min-h-touch items-center gap-1 border-t border-line px-5 py-3 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4283" data-mp="2tzpdf7i685ch">
        View all nearby
        <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-4284" data-mp="38l38d7b5l6qp" />
      </Link>
    </section>;
}

/** Recent reports — the evidence itself, with its provenance stated. */
export function RecentReports({
  limit = 5
}: {
  limit?: number;
}) {
  const {
    reports
  } = usePower();
  const visible = reports.filter(report => {
    const location = powerLocationById(report.locationId);
    // One person's accommodation is never published in a community feed.
    return location && !location.personal;
  }).slice(0, limit);
  return <section aria-labelledby="power-reports" className="flex flex-col rounded-2xl border border-line bg-surface shadow-panel" data-id="element-4285" data-mp="2kv8xgvae9s93">
      <div className="p-5 pb-3" data-id="element-4286" data-mp="2ayw3rxeuneec">
        <h2 id="power-reports" className="text-h4 font-semibold text-ink" data-id="element-4287" data-mp="2azp004g6mli8">
          Recent reports
        </h2>
        <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-4288" data-mp="26vzgvuia0w4l">Latest from your community</p>
      </div>
      {visible.length === 0 ? <p className="px-5 pb-5 text-body-sm text-ink-muted" data-id="element-4289" data-mp="hfnf1i39p8ry">
          No reports yet. The first one starts the record for your campus.
        </p> : <ul className="divide-y divide-line" data-id="element-4290" data-mp="md6w0prcnznl">
          {visible.map(report => {
        const location = powerLocationById(report.locationId);
        if (!location) return null;
        const Icon = powerIcons[LOCATION_ICON[location.kind]];
        return <li key={report.id} className="flex items-center gap-3 px-5 py-2.5" data-id="element-4291" data-mp="3uvl676btskrb">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-canvas text-ink-muted" data-id="element-4292" data-mp="2pzfalaqii6c">
                  <Icon className="h-4 w-4" aria-hidden data-id="element-4293" data-mp="7p29tbt39602" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-4294" data-mp="cgu3vwbgdtuy">
                  <span className="block truncate text-body-sm font-medium text-ink" data-id="element-4295" data-mp="cievpe6duyp4">
                    {location.name}
                  </span>
                  <span className="block truncate text-caption text-ink-subtle" data-id="element-4296" data-mp="3g4vrvv32ipj8">
                    by {report.own ? 'you' : EVIDENCE_LABEL[report.evidence]}
                  </span>
                </span>
                <PowerStatusPill state={report.state} size="xs" data-id="element-4297" data-mp="24k9ww7rq132j" />
                <span className="cz-num hidden shrink-0 text-caption text-ink-subtle sm:block" data-id="element-4298" data-mp="2iiri9yu5m3ha">
                  {freshnessLabel(report.reportedAt)}
                </span>
              </li>;
      })}
        </ul>}
      <Link to="/power/reports" className="mt-auto flex min-h-touch items-center gap-1 border-t border-line px-5 py-3 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4299" data-mp="2sg5nf2amn5ua">
        View all reports
        <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-4300" data-mp="32679xpnc0g73" />
      </Link>
    </section>;
}

/**
 * Reliability. Deliberately refuses to draw a chart until there is enough
 * history: a percentage from a handful of reports is a fabrication with a
 * decimal point on it.
 */
export function PowerReliabilityPanel({
  locationId
}: {
  locationId: string;
}) {
  const {
    getReliability
  } = usePower();
  const location = powerLocationById(locationId);
  const reliability = getReliability(locationId);
  return <section aria-labelledby="power-reliability" className="relative overflow-hidden rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4301" data-mp="3usp4u0421xrs">
      <div className="flex items-center gap-2" data-id="element-4302" data-mp="3ku9ehzcuqsjc">
        <h2 id="power-reliability" className="text-h4 font-semibold text-ink" data-id="element-4303" data-mp="3kx5dlr0frn9d">
          Power reliability
        </h2>
        <span className="rounded-full border border-brand-100 bg-brand-50 px-2 py-0.5 text-caption font-semibold uppercase tracking-wide text-brand-800" data-id="element-4304" data-mp="3bucl5kfip5hy">
          Beta
        </span>
      </div>
      <p className="mt-2 text-body-sm font-medium text-ink" data-id="element-4305" data-mp="324t3uj7gdjp7">{location?.name ?? 'Location'}</p>

      {reliability.sufficient ? <>
          <p className="cz-num mt-1 text-h2 font-semibold text-ink" data-id="element-4306" data-mp="2o6cvkntxzl4y">
            {Math.round((reliability.uptimeShare ?? 0) * 100)}%
          </p>
          <p className="text-caption text-ink-subtle" data-id="element-4307" data-mp="1l3dt5v94gb89">
            Powered in {reliability.reportCount} reports across {reliability.sampleDays} days.
          </p>
        </> : <>
          <p className="mt-1 text-body-sm text-ink-muted" data-id="element-4308" data-mp="1c2pecybwm6ub">Not enough data yet</p>
          <p className="mt-1 max-w-[36ch] text-caption text-ink-subtle" data-id="element-4309" data-mp="1c4sfadelhkdt">
            Reliability needs at least {RELIABILITY_MINIMUM.reports} reports across{' '}
            {RELIABILITY_MINIMUM.days} days. {reliability.reportCount} recorded so far.
          </p>
        </>}

      <img src={RELIABILITY_ART} alt="" aria-hidden className="pointer-events-none absolute -bottom-2 right-0 hidden h-28 w-28 object-contain opacity-90 sm:block" data-id="element-4310" data-mp="28sgmi0d4bsil" />
      <Link to="/power/reports" className="relative mt-4 inline-flex min-h-touch items-center rounded-xl border border-line bg-canvas px-4 text-body-sm font-medium text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-4311" data-mp="1zytricgqitag">
        View details
      </Link>
    </section>;
}

/** How it works — part of the product, not documentation. */
export function HowItWorks() {
  const steps = [{
    title: 'Report',
    body: 'Tell us what is happening where you are.'
  }, {
    title: 'Aggregate',
    body: 'We weigh reports from the community against each other.'
  }, {
    title: 'Inform',
    body: 'You see a status with its confidence and its age.'
  }];
  return <section aria-labelledby="power-how" className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4312" data-mp="2evl645d7xq58">
      <h2 id="power-how" className="text-h4 font-semibold text-ink" data-id="element-4313" data-mp="2sw4e5choow7e">
        How it works
      </h2>
      <ol className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-4314" data-mp="2nv88atdruzzc">
        {steps.map((step, index) => <li key={step.title} className="flex gap-3" data-id="element-4315" data-mp="83ssx35tt0y2" data-mpi={index}>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-canvas text-caption font-semibold text-ink-muted" data-id="element-4316" data-mp="35r74ah0mniw">
              {index + 1}
            </span>
            <span data-id="element-4317" data-mp="b8t46is8izkz">
              <span className="block text-body-sm font-semibold text-ink" data-id="element-4318" data-mp="f7s943dyuwcf">{step.title}</span>
              <span className="mt-0.5 block text-caption text-ink-subtle" data-id="element-4319" data-mp="k7eks8e5c4j4">{step.body}</span>
            </span>
          </li>)}
      </ol>
    </section>;
}

/** Your impact — counted from the reports you actually filed. */
export function YourImpact({
  onReport
}: {
  onReport: () => void;
}) {
  const {
    contributionPoints,
    pointsThisWeek,
    ownReports
  } = usePower();
  return <section aria-labelledby="power-impact" className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4320" data-mp="3sklu4opi12ih">
      <h2 id="power-impact" className="text-h4 font-semibold text-ink" data-id="element-4321" data-mp="67nc8n6efgci">
        Your impact
      </h2>
      {ownReports.length === 0 ? <>
          <p className="mt-1 max-w-[42ch] text-body-sm text-ink-muted" data-id="element-4322" data-mp="34v9n9vhn96jc">
            You have not reported power yet. Your first report starts your contribution
            record and helps everyone heading the same way.
          </p>
          <button type="button" onClick={onReport} className="mt-4 inline-flex min-h-touch items-center rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-4323" data-mp="2xe4jcomoqewo">
            Report power
          </button>
        </> : <>
          <p className="mt-1 max-w-[42ch] text-body-sm text-ink-muted" data-id="element-4324" data-mp="2jfemna3s2h8u">
            Thanks for helping your campus community stay informed.
          </p>
          <div className="mt-4 flex items-end gap-3 rounded-xl border border-line bg-canvas px-4 py-3" data-id="element-4325" data-mp="29nh7xtdk975d">
            <div data-id="element-4326" data-mp="24pyxtznt4lvs">
              <p className="text-caption text-ink-subtle" data-id="element-4327" data-mp="24qigxjp71gbe">Contribution points</p>
              <p className="cz-num text-h2 font-semibold leading-tight text-ink" data-id="element-4328" data-mp="1zp1e3in5fjs6">
                {contributionPoints}
              </p>
            </div>
            {pointsThisWeek > 0 && <span className="mb-1 rounded-full border border-ok-100 bg-ok-50 px-2 py-0.5 text-caption font-semibold text-ok-800" data-id="element-4329" data-mp="2kevlc9un4xpu">
                +{pointsThisWeek} this week
              </span>}
          </div>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-4330" data-mp="2bmbu3ugohvf1">
            Five points for each report Campuzon accepted. Points are a thank-you, not a
            ranking.
          </p>
        </>}
    </section>;
}
export function PanelSkeleton({
  rows = 4
}: {
  rows?: number;
}) {
  return <div className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4331" data-mp="26oju0d5vr2zr">
      <PowerSkeleton className="h-6 w-36" data-id="element-4332" data-mp="1sp0w0d6s9816" />
      <div className="mt-4 space-y-2.5" data-id="element-4333" data-mp="1sq29spf6o8ml">
        {Array.from({
        length: rows
      }).map((_, index) => <PowerSkeleton key={index} className="h-11" data-id="element-4334" data-mp="1wre06x5vsxvu" data-mpi={index} />)}
      </div>
    </div>;
}